import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import socket, { ensureSocketReady } from '../socket';
import { useAuth } from '../context/AuthContext';
import Input from './ui/Input';
import Select from './ui/Select';
import Button from './ui/Button';
import Card from './ui/Card';
import { roomShareUrl } from '../site';
import { MAX_USER_NAME_LENGTH, clampUserName } from '../constants/limits';

function CreateRoom() {
    const { user } = useAuth();
    const [roomName, setRoomName] = useState(() => clampUserName(user?.name || ''));
    const [roomId, setRoomId] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [createdRoomLink, setCreatedRoomLink] = useState('');
    const [sequenceType, setSequenceType] = useState('sequential');
    const [role, setRole] = useState('DEV');
    const navigate = useNavigate();

    useEffect(() => {
        if (user?.name) {
            setRoomName((prev) => prev || clampUserName(user.name));
        }
    }, [user]);

    useEffect(() => {
        const onAuthError = ({ message }) => {
            setIsCreating(false);
            alert(message || 'Faça login novamente para criar a sala.');
        };
        const onConnectError = () => {
            setIsCreating(false);
        };
        socket.on('authError', onAuthError);
        socket.on('connect_error', onConnectError);
        return () => {
            socket.off('authError', onAuthError);
            socket.off('connect_error', onConnectError);
        };
    }, []);

    const generateSequence = (type) => {
        let sequence = [];
        if (type === 'sequential') {
            sequence = Array.from({ length: 40 }, (_, i) => String(i));
        } else if (type === 'even') {
            sequence = Array.from({ length: 40 }, (_, i) => String(i * 2));
        } else if (type === 'fibonacci') {
            sequence = [0, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144, 233, 377, 610, 987];
        }
        return sequence.concat(['?', '☕']);
    };

    const handleCreateRoom = async () => {
        if (roomName.trim() === '') {
            alert('Digite um nome para sala!');
            return;
        }

        const trimmedName = clampUserName(roomName);

        setIsCreating(true);
        const sequence = generateSequence(sequenceType);
        let timeoutId;

        const resetCreating = () => {
            clearTimeout(timeoutId);
            setIsCreating(false);
        };

        try {
            await ensureSocketReady();

            socket.off('roomCreated');
            socket.once('roomCreated', ({ roomId: newRoomId }) => {
                resetCreating();
                setRoomId(newRoomId);
                setCreatedRoomLink(roomShareUrl(newRoomId));
                localStorage.setItem('roomId', newRoomId);
                localStorage.setItem('userName', trimmedName);
                localStorage.setItem('userRole', role);
                navigate(`/room/${newRoomId}`);
            });

            timeoutId = setTimeout(() => {
                socket.off('roomCreated');
                resetCreating();
                alert('Não foi possível criar a sala. Verifique sua conexão e tente novamente.');
            }, 15000);

            socket.emit('createRoom', { roomName: trimmedName, role, userRole: role, sequence });
        } catch {
            resetCreating();
            alert('Não foi possível conectar ao servidor. Tente novamente.');
        }
    };

    const voltarHome = () => navigate('/');

    return (
        <div className="flex items-center justify-center w-full max-w-md mx-auto mt-8 z-10">
            <Card className="p-8 w-full">
                {!createdRoomLink ? (
                    <>
                        <h2 className="text-2xl font-bold text-slate-100 mb-6 text-center">
                            ✨ Criar Nova Sala
                        </h2>

                        <div className="space-y-5">
                            <Input
                                label="Seu Nome"
                                type="text"
                                placeholder="Digite seu nome"
                                value={roomName}
                                onChange={(e) => setRoomName(e.target.value)}
                                maxLength={MAX_USER_NAME_LENGTH}
                                required
                            />

                            <Select
                                label="Tipo de Sequência"
                                value={sequenceType}
                                onChange={(e) => setSequenceType(e.target.value)}
                            >
                                <option value="sequential">Sequencial padrão (0,1,2,3...)</option>
                                <option value="even">Pares padrão (0,2,4,6...)</option>
                                <option value="fibonacci">Fibonacci</option>
                            </Select>

                            <Select
                                label="Seu Papel"
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                            >
                                <option value="DEV">Dev</option>
                                <option value="QA">QA</option>
                                <option value="DEVOPS">DevOps</option>
                                <option value="PO">PO</option>
                                <option value="LEADER">Leader</option>
                                <option value="OUTROS">Outros</option>
                            </Select>

                            <div className="flex flex-col sm:flex-row gap-3 pt-4">
                                <Button
                                    variant="primary"
                                    onClick={handleCreateRoom}
                                    disabled={isCreating}
                                    loading={isCreating}
                                    className="flex-1"
                                >
                                    {isCreating ? 'Criando...' : '✨ Criar Sala'}
                                </Button>
                                <Button
                                    variant="ghost"
                                    onClick={voltarHome}
                                    className="flex-1"
                                >
                                    ← Voltar
                                </Button>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="text-center">
                            <div className="mb-4 text-5xl">✅</div>
                            <h3 className="text-xl font-semibold text-slate-100 mb-2">
                                Sala criada com sucesso!
                            </h3>
                            <p className="text-slate-300 mb-6">
                                ID: <strong className="text-primary-light">{roomId}</strong>
                            </p>
                            <Button
                                variant="primary"
                                onClick={voltarHome}
                                className="w-full"
                            >
                                ← Voltar para Home
                            </Button>
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
}

export default CreateRoom;
